import { useEffect, useState } from "react";
import { useStore } from "react-redux";
import { useAppDispatch, useAppSelector } from "@/state/hooks";
import type { RootState } from "@/state/store";
import type { Tab } from "@/state/slices/tabsSlice";
import type { ChartTheme, UiTabState } from "@/state/slices/uiSlice";
import type { FibLevel } from "@/api/types";
import {
  fetchWorkspace,
  fetchWorkspaceList,
  saveWorkspace,
} from "@/state/slices/workspacesSlice";
import { fetchOhlc } from "@/state/slices/ohlcSlice";
import type {
  WorkspacePayload,
  WorkspaceTabState,
} from "@/api/types";
import { initialMaxFiles, isHtfTimeframe } from "@/utils/timeframe";
import { apiUrl } from "@/api/client";
import { getClientId } from "@/utils/clientId";

// Editing-inactivity window before we PUT to the server. 3s (was 10s)
// so workspace live-sync feels responsive — another session sees an
// edit ~3.5s after you stop, since the SSE broadcast rides on the
// save. Still inactivity-based: mid-drag changes re-arm the timer (no
// mid-gesture saves) and an unchanged hash sends nothing at all.
// Viewport changes re-arm this timer but are stripped from the
// change-detection hash below, so panning never causes a network save
// on its own. The page-hide flush captures the latest viewport when
// the user leaves.
const AUTOSAVE_DEBOUNCE_MS = 3000;

// Structural-change debounce — applies when the user adds /
// removes / renames / re-pane-moves a tab, or activates a
// different one. Much shorter than the content debounce because
// the failure mode (user refreshes in <10s and loses the new tab)
// is more surprising than the cost of a 1.5s-faster save.
const AUTOSAVE_STRUCTURAL_MS = 1500;

/** Convert the live redux state into the payload the workspace
 *  endpoint expects. Pure — no side effects — so it's easy to call
 *  both from the autosave hook and from a "save as new" flow. */
export function buildWorkspacePayload(
  tabs: Tab[],
  activeTabId: string,
  uiByTab: Record<string, UiTabState>,
  splitTabId: string | null = null,
  splitRatio: number = 0.5,
  paneByTabId: Record<string, 0 | 1> = {},
  prefs: {
    plainDarkChart: boolean;
    monoCandles: boolean;
    theme?: ChartTheme;
    /** Per-scheme last-saved customisations. Round-tripped via
     *  the workspace JSON so the Properties dialog can replay
     *  each preset's user tweaks on reopen. */
    themeCache?: Record<string, ChartTheme | undefined>;
    /** Workspace-scope fib levels override. Null = inherit. */
    fibLevels?: FibLevel[] | null;
  } = {
    plainDarkChart: false,
    monoCandles: false,
  },
): WorkspacePayload {
  const byTab: Record<string, WorkspaceTabState> = {};
  for (const tab of tabs) {
    const ui = uiByTab[tab.id];
    if (!ui) continue;
    byTab[tab.id] = {
      symbol: ui.symbol,
      timeframe: ui.timeframe,
      analysisStart: ui.analysisStart,
      analysisEnd: ui.analysisEnd,
      chartType: ui.chartType,
      drawings: {
        fibs: ui.fibRetracements,
        horizontal_lines: ui.horizontalLines,
        vertical_lines: ui.verticalLines,
        diagonal_lines: ui.diagonalLines,
        paths: ui.paths,
        positions: ui.positions,
        price_ranges: ui.priceRangeBoxes,
        text_labels: ui.textLabels,
      },
      viewport: ui.viewport,
      scaleMargins: ui.scaleMargins,
      priceRange: ui.priceRange,
      showTradingTrades: ui.showTradingTrades,
      showTradingSlTp: ui.showTradingSlTp,
      showHistoryOrders: ui.showHistoryOrders,
      // Per-tab theme + cache + fib overrides. null/undefined =
      // inherit from User scope (or built-in defaults). theme/themeCache
      // are the DARK-mode slot; themeLight/themeCacheLight the LIGHT one.
      theme: ui.theme ? (ui.theme as unknown as Record<string, unknown>) : null,
      themeCache: ui.themeCache as
        | Record<string, Record<string, unknown>>
        | undefined,
      themeLight: ui.themeLight
        ? (ui.themeLight as unknown as Record<string, unknown>)
        : null,
      themeCacheLight: ui.themeCacheLight as
        | Record<string, Record<string, unknown>>
        | undefined,
      fibLevels: ui.fibLevels ?? null,
      fibLineWidth: ui.fibLineWidth ?? null,
      fibLineStyle: ui.fibLineStyle ?? null,
      // Per-tab Weekly Behaviour override (session bands).
      weeklyBehaviour: ui.weeklyBehaviour
        ? (ui.weeklyBehaviour as unknown as Record<string, unknown>)
        : null,
      indicators: ui.indicators
        ? (ui.indicators as unknown as Record<string, unknown>)
        : null,
    };
  }
  return {
    version: 1,
    tabs: tabs.map((t) => ({
      id: t.id,
      ...(t.label != null ? { label: t.label } : { label: null }),
      ...(t.locked ? { locked: true } : {}),
    })),
    activeTabId,
    byTab,
    splitTabId,
    splitRatio,
    paneByTabId,
    prefs,
    // Live-sync own-echo suppression: the server broadcasts this back
    // with the save poke so the originating session can ignore it.
    client_id: getClientId(),
  };
}

/** Strip per-tab viewport, scaleMargins AND priceRange from the hash
 *  so change-detection ignores pan/zoom/scaling/price-drag updates on
 *  their own. All three still ride along in the actual PUT body when
 *  a save fires — they're just not what triggers one. Same rationale:
 *  a user idly scrolling or dragging the chart shouldn't cost a
 *  network round-trip every 10s.
 *
 *  Everything else from the payload IS included so it's correctly
 *  diffed: splitRatio (drag-divider), splitTabId / paneByTabId
 *  (right-pane open / move), prefs (workspace-wide toggles like
 *  plain-dark / mono candles). Earlier versions left these out and
 *  silently dropped split-divider drags + pref toggles. */
function hashWithoutViewport(p: WorkspacePayload): string {
  const stripped = {
    version: p.version,
    activeTabId: p.activeTabId,
    tabs: p.tabs,
    splitTabId: p.splitTabId,
    splitRatio: p.splitRatio,
    paneByTabId: p.paneByTabId,
    prefs: p.prefs,
    byTab: Object.fromEntries(
      Object.entries(p.byTab).map(([id, t]) => {
        const {
          viewport: _vp,
          scaleMargins: _sm,
          priceRange: _pr,
          ...rest
        } = t;
        return [id, rest];
      }),
    ),
  };
  return JSON.stringify(stripped);
}

// Last hash we sent to the server. Module-level (the autosave hook is
// mounted exactly once, in App) so other hooks — live-sync — can ask
// "does this session have unsaved local edits?" without threading refs.
// The baseline-reset effect below repopulates it after every hydrate /
// workspace switch.
let lastSavedHash = "";

/** True when the current redux state differs from the last snapshot
 *  this session saved (viewport/scale/price-range excluded, same as
 *  the autosave's own change detection). Live-sync uses this to avoid
 *  clobbering a newer local edit with an inbound remote hydrate. */
export function isWorkspaceDirty(state: RootState): boolean {
  if (!state.workspaces.currentName) return false;
  const payload = buildWorkspacePayload(
    state.tabs.tabs,
    state.tabs.activeTabId,
    state.ui.byTab,
    state.tabs.splitTabId,
    state.tabs.splitRatio,
    state.tabs.paneByTabId,
    state.ui.prefs,
  );
  return hashWithoutViewport(payload) !== lastSavedHash;
}

/** Debounced auto-save of the current workspace to the server.
 *  Watches tabs + per-tab ui state and re-arms a 10s timer on every
 *  change; when the timer fires, the snapshot is hashed (excluding
 *  viewport) and PUT only if the hash differs from the last save.
 *  Panning re-arms the timer but never causes a network call on its
 *  own.
 *
 *  Skipped when:
 *  - `isHydrating` is true (we're replaying server state — saving
 *    back would race with the in-progress hydrate).
 *  - `currentName` is null (no workspace selected — nothing to save
 *    into; user must Create a workspace from the switcher first). */
export function useWorkspaceAutoSave(): void {
  const dispatch = useAppDispatch();
  const store = useStore<RootState>();
  const currentName = useAppSelector((s) => s.workspaces.currentName);
  const isHydrating = useAppSelector((s) => s.workspaces.isHydrating);
  const tabs = useAppSelector((s) => s.tabs.tabs);
  const activeTabId = useAppSelector((s) => s.tabs.activeTabId);
  const splitTabId = useAppSelector((s) => s.tabs.splitTabId);
  const splitRatio = useAppSelector((s) => s.tabs.splitRatio);
  const paneByTabId = useAppSelector((s) => s.tabs.paneByTabId);
  const uiByTab = useAppSelector((s) => s.ui.byTab);
  const prefs = useAppSelector((s) => s.ui.prefs);

  // Reset the baseline whenever we switch workspaces or finish
  // hydrating — otherwise a stale hash from the previous workspace
  // would falsely suppress the first save of the new one.
  useEffect(() => {
    if (!isHydrating && currentName) {
      const s = store.getState();
      const payload = buildWorkspacePayload(
        s.tabs.tabs,
        s.tabs.activeTabId,
        s.ui.byTab,
        s.tabs.splitTabId,
        s.tabs.splitRatio,
        s.tabs.paneByTabId,
        s.ui.prefs,
      );
      lastSavedHash = hashWithoutViewport(payload);
    }
  }, [store, currentName, isHydrating]);

  /** Build + diff + PUT. Shared body between the two debounced
   *  effects below so structural and content saves share a single
   *  hash baseline (no double-saves). */
  const tryFlush = () => {
    const state = store.getState();
    // A save is already in-flight — bail out so a concurrent structural +
    // content debounce firing close together doesn't send a second PUT with
    // the same stale revision and land a 409. The next state change re-arms
    // both debounces; the pagehide flush captures any trailing change on close.
    if (state.workspaces.saving) return;
    const payload = buildWorkspacePayload(
      state.tabs.tabs,
      state.tabs.activeTabId,
      state.ui.byTab,
      state.tabs.splitTabId,
      state.tabs.splitRatio,
      state.tabs.paneByTabId,
      state.ui.prefs,
    );
    const hash = hashWithoutViewport(payload);
    if (hash === lastSavedHash) return;
    lastSavedHash = hash;
    dispatch(saveWorkspace({ name: currentName!, payload }));
  };

  // Structural changes — fast debounce. Watches the tab list,
  // active/split tab ids, and pane mapping. The short 1.5s timer
  // is the line of defence: it fires quickly enough that a manual
  // page refresh after a structural edit catches it.
  //
  // We deliberately don't flush-on-cleanup when isHydrating goes
  // true — doing so kicks off a PUT concurrent with the incoming
  // GET, and if the GET wins the race the local just-added tab
  // gets clobbered by the stale on-disk state. Letting the timer
  // be cancelled means edits made within the debounce window of a
  // workspace switch CAN be lost — that's the cost of the simpler,
  // race-free design.
  useEffect(() => {
    if (isHydrating || !currentName) return;
    const handle = window.setTimeout(tryFlush, AUTOSAVE_STRUCTURAL_MS);
    return () => window.clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dispatch,
    store,
    currentName,
    isHydrating,
    tabs,
    activeTabId,
    splitTabId,
    splitRatio,
    paneByTabId,
    prefs,
  ]);

  // Content changes — slow debounce. Viewport/scaleMargins/
  // priceRange are stripped from the hash so idle panning doesn't
  // trigger a save; drawings + symbol/timeframe etc. do.
  useEffect(() => {
    if (isHydrating || !currentName) return;
    const handle = window.setTimeout(tryFlush, AUTOSAVE_DEBOUNCE_MS);
    return () => window.clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, store, currentName, isHydrating, uiByTab]);
}

/** Page-close flush: when the user navigates away / closes the tab /
 *  the OS suspends the browser, fire one final PUT carrying the
 *  current state (viewport included). `fetch` with `keepalive: true`
 *  lets the request finish even after the document is gone — capped
 *  at ~64 KB body, which a workspace is well within.
 *
 *  Listens to `pagehide` only (covers close, same-tab nav, and mobile
 *  tab suspend). The previous `visibilitychange → hidden` listener was
 *  removed: it fired on every alt-tab / app-switch, bumping the server
 *  revision via a raw fetch that bypassed Redux — the next debounced
 *  autosave then sent the stale revision and landed a 409 on every
 *  single app-switch (issue #45). On desktop the autosave debounce
 *  keeps running while the tab is backgrounded, so the flush is not
 *  needed there. */
export function useWorkspaceFlushOnHide(): void {
  const store = useStore<RootState>();
  const currentName = useAppSelector((s) => s.workspaces.currentName);

  useEffect(() => {
    if (!currentName) return;
    const flush = () => {
      const state = store.getState();
      const payload = buildWorkspacePayload(
        state.tabs.tabs,
        state.tabs.activeTabId,
        state.ui.byTab,
        state.tabs.splitTabId,
        state.tabs.splitRatio,
        state.tabs.paneByTabId,
        state.ui.prefs,
      );
      // Stamp the known server revision — the backend's optimistic-
      // concurrency check 409s a PUT whose revision doesn't match, so a
      // revision-less flush silently fails and last-second edits (e.g.
      // a Chart Properties change then immediate reload) are lost. The
      // debounced autosave goes through the saveWorkspace thunk which
      // stamps this for us; the raw keepalive fetch must do it itself.
      payload.revision = state.workspaces.revisionByName[currentName] ?? 0;
      try {
        // `fetch` rejects ASYNCHRONOUSLY (e.g. server down during a
        // restart, or a network blip while the tab is hidden), so a
        // surrounding try/catch can't catch it — the rejection would
        // surface as "Uncaught (in promise) TypeError: Failed to
        // fetch". Swallow it on the promise. The try/catch still
        // guards the synchronous JSON.stringify / fetch-arg setup.
        // Must target the API host (apiUrl) + send the session cookie:
        // in the split deployment the app + API are on different
        // subdomains, so a bare same-origin path hits the static client
        // server (405) and the flush silently fails — i.e. last-second
        // changes (e.g. a view toggle then immediate reload) get lost.
        void fetch(apiUrl(`/api/workspaces/${encodeURIComponent(currentName)}`), {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
          credentials: "include",
          keepalive: true,
        }).catch(() => {
          // Best-effort during unload — no UI to surface errors to.
        });
      } catch {
        // Best-effort during unload — no UI to surface errors to.
      }
    };
    window.addEventListener("pagehide", flush);
    return () => {
      window.removeEventListener("pagehide", flush);
    };
  }, [store, currentName]);
}

/** App-boot bootstrap: fetch the workspace list and auto-load the
 *  server-recorded active workspace (if any). Returns `ready` so the
 *  caller can hold off rendering UI that would auto-save default
 *  state — though in practice the autosave hook's `currentName` guard
 *  is enough, so `ready` is mostly informational. */
export function useWorkspaceBoot(): { ready: boolean } {
  const dispatch = useAppDispatch();
  const meStatus = useAppSelector((s) => s.me.status);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    // Hold off until the user is authenticated. Firing the workspace
    // fetch before /api/me resolves just 401s and logs a scary
    // "Workspace boot failed" warning on the login screen.
    if (meStatus !== "succeeded") return;
    let cancelled = false;
    (async () => {
      try {
        const listResp = await dispatch(fetchWorkspaceList()).unwrap();
        if (cancelled) return;
        const active = listResp.active;
        if (active) {
          const wsResult = await dispatch(fetchWorkspace(active)).unwrap();
          if (cancelled) return;
          // Pre-warm OHLC for every workspace tab now that hydrateWorkspace
          // has fired (fetchWorkspace dispatches it internally before
          // returning).  Starting the fetches here — before Chart components
          // mount in the next render — means the `status === "loading"` guard
          // in each Chart's fetch effect blocks duplicate dispatches when those
          // components do mount, so all tabs load their data in parallel
          // without a second round-trip.
          for (const [tabId, tabState] of Object.entries(wsResult.payload.byTab)) {
            const isHtf = isHtfTimeframe(tabState.timeframe);
            let maxFiles = initialMaxFiles(tabState.timeframe);
            // Mirror the viewport-anchoring logic from Chart.tsx so the
            // pre-warm covers the user's saved viewport left edge.
            if (!isHtf) {
              const vp = tabState.viewport;
              if (vp && Number.isFinite(vp.from)) {
                const daysBack = Math.min(
                  800,
                  Math.ceil((Date.now() / 1000 - vp.from) / 86400) + 5,
                );
                if (daysBack > maxFiles) maxFiles = daysBack;
              }
            }
            dispatch(
              fetchOhlc({
                tabId,
                request: {
                  symbol: tabState.symbol,
                  timeframe: tabState.timeframe,
                  max_files: maxFiles,
                },
              }),
            );
          }
        }
      } catch (err) {
        // Boot failure shouldn't block the app — the default tab is
        // still usable, the user can create a workspace later from
        // the switcher. Surface to the console for debugging.
        // eslint-disable-next-line no-console
        console.warn("Workspace boot failed:", err);
      } finally {
        if (!cancelled) setReady(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [dispatch, meStatus]);

  return { ready };
}
